import React, { useCallback, useEffect, useRef, useState } from "react";
import { useUnityContext } from "react-unity-webgl";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import VoiceRecorder from "./VoiceRecorder";
import ViewerHeader from "./ViewerHeader";
import EnvironmentMap from "./EnvironmentMap";
import Label from "./Label";
import MicAccessPopup from "./MicAccessPopup";
import StartGuideDialog from "./StartGuideDialog";

import ExperiencesAPI from "../services/ExperiencesAPI";
import { GAME_STATES, GameStateProvider, useGameState } from "../context/gameState";

import useWebGpuDebug from "./unityWebViewer/useWebGpuDebug";
import useUnityEventBindings from "./unityWebViewer/useUnityEventBindings";
import MobileFallback from "./unityWebViewer/MobileFallback";
import StatusOverlays from "./unityWebViewer/StatusOverlays";
import EdgeCameraControls from "./unityWebViewer/EdgeCameraControls";
import UnityCanvas from "./unityWebViewer/UnityCanvas";

const UNITY_BUILD_PREFIX = "/webplayer/10kScienceWeb_1.2.31p4_Production";

const UnityWebViewerContent = ({ experienceId, embed }) => {
  const voiceRecorderRef = useRef(null);
  const [currentElement, setCurrentElement] = useState(null);
  const [systemData, setSystemData] = useState(null);
  const [focusedElement, setFocusedElement] = useState(null);
  const [focusedPanelElementLocked, setFocusedPanelElementLocked] = useState(null);
  const [labelPosition, setLabelPosition] = useState(null);
  const [currentEnvironment, setCurrentEnvironment] = useState(null);
  const [systemLoaded, setSystemLoaded] = useState(false);
  const [experienceData, setExperienceData] = useState(null);
  const [layoutOverridesAll, setLayoutOverridesAll] = useState(null);
  const [layoutOverrides, setLayoutOverrides] = useState(null);
  const [cursorStyle, setCursorStyle] = useState("default");
  const focusTimeoutRef = useRef(null);
  const latestMousePositionRef = useRef({ x: 0, y: 0 });
  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [instructionsVisible, setInstructionVisible] = useState(false);
  const [showControlsOverlay, setShowControlsOverlay] = useState(false);
  const [edgeHover, setEdgeHover] = useState({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });
  const edgeHoverRef = useRef({
    left: false,
    right: false,
    top: false,
    bottom: false,
  });

  const [showEnvironmentLabel, setShowEnvironmentLabel] = useState(false);
  const environmentLabelTimeoutRef = useRef(null);
  const { currentGameState, setGameState, isCurrentGameState } = useGameState();

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const overlayWidth = isPhone ? "92vw" : isTablet ? "70vw" : "540px";
  const overlayPadding = isPhone ? "6px 12px" : "8px 16px";
  const topMessageOffset = isPhone ? "70px" : "90px";
  const envLabelBottom = isPhone ? "100px" : "150px";
  const edgeControlWidth = isPhone ? 48 : 60;
  const edgeControlHeight = isPhone ? 46 : 54;

  useWebGpuDebug();

  const { unityProvider, sendMessage, addEventListener, removeEventListener } =
    useUnityContext({
      loaderUrl: `${UNITY_BUILD_PREFIX}.loader.js`,
      dataUrl: `${UNITY_BUILD_PREFIX}.data`,
      frameworkUrl: `${UNITY_BUILD_PREFIX}.framework.js`,
      codeUrl: `${UNITY_BUILD_PREFIX}.wasm`,
    });

  const playerOffset = 0;
  const dragStart = useRef(null);

  const sendMessageToUnity = useCallback(
    (gameObject, methodName, value) => {
      sendMessage(gameObject, methodName, value);
    },
    [sendMessage]
  );

  const handleElementSelected = useCallback((elementData) => {
    const parsedElement =
      typeof elementData === "string" ? JSON.parse(elementData) : elementData;
    setCurrentElement(parsedElement);
  }, []);

  const handleInsightStarted = useCallback((insightId) => {
    ExperiencesAPI.get(`insights/${insightId}`)
      .then((response) => {
        setCurrentInsight(response.data.data);
      })
      .catch((error) => {
        console.error("Failed to fetch insight data", error);
      });
  }, []);

  const handleGameStateChanged = useCallback((gameStateName) => {
    setGameState(gameStateName);
  }, [setGameState]);

  const handleAssessmentComplete = useCallback((assessmentId) => {
    setTimeout(() => {
      ExperiencesAPI.get(`assessments/${assessmentId}`)
        .catch((error) => {
          console.error("Failed to fetch assessment data", error);
        });
    }, 3000);
  }, []);

  const handleInstructionVisibilityChanged = useCallback((visible) => {
    setInstructionVisible(visible.toLowerCase() === "true");
  }, []);

  const handleEnvironmentChanged = useCallback((environmentData) => {
    setEnvironmentsVisited((prev) => {
      const next = prev + 1;
      // Show overlay only after the third environment loads (first change is auto on start).
      setShowControlsOverlay(next === 3);
      return next;
    });

    try {
      const parsedEnvironment =
        typeof environmentData === "string"
          ? JSON.parse(environmentData)
          : environmentData;
      setFocusedElement(null);
      setCurrentEnvironment(parsedEnvironment);

      if (environmentLabelTimeoutRef.current) {
        clearTimeout(environmentLabelTimeoutRef.current);
      }

      setShowEnvironmentLabel(true);

      environmentLabelTimeoutRef.current = setTimeout(() => {
        setShowEnvironmentLabel(false);
        environmentLabelTimeoutRef.current = null;
      }, 2500);
    } catch (error) {
      console.error("Failed to parse environmentData", error);
    }
  }, []);

  const handleRecordingStarted = useCallback((instanceId) => {
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.startRecording(instanceId);
    }
  }, []);

  const handleUploadAudio = useCallback((presignedUrl) => {
    if (voiceRecorderRef.current) {
      voiceRecorderRef.current.uploadFileToS3(presignedUrl);
    }
  }, []);

  const handleReadyToLoadExperience = useCallback(() => {
    sendMessageToUnity("EventDispatcher", "LoadExperience", experienceId);
  }, [experienceId, sendMessageToUnity]);

  const handleSystemLoaded = useCallback((systemDataValue) => {
    const parsedSystem =
      typeof systemDataValue === "string"
        ? JSON.parse(systemDataValue)
        : systemDataValue;
    setSystemData(parsedSystem);
  }, []);

  const handleSystemFullyLoaded = useCallback(() => {
    setSystemLoaded(true);
  }, []);

  const handleElementDeselected = useCallback(() => {
    setCurrentElement(null);
  }, []);

  useUnityEventBindings({
    addEventListener,
    removeEventListener,
    onElementSelected: handleElementSelected,
    onElementDeselected: handleElementDeselected,
    onEnvironmentChanged: handleEnvironmentChanged,
    onRecordingStarted: handleRecordingStarted,
    onUploadAudioFile: handleUploadAudio,
    onReadyToLoadExperience: handleReadyToLoadExperience,
    onSystemLoaded: handleSystemLoaded,
    onSystemFullyLoaded: handleSystemFullyLoaded,
    onAssessmentComplete: handleAssessmentComplete,
    onInstructionVisibilityChanged: handleInstructionVisibilityChanged,
    onInsightStarted: handleInsightStarted,
    onGameStateChanged: handleGameStateChanged,
  });

  useEffect(() => {
    if (experienceId) {
      ExperiencesAPI.get(`experiences/${experienceId}`).then((response) => {
        setExperienceData(response.data);
      });
    }
  }, [experienceId]);

  // Load optional local layout overrides for the environment map.
  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const response = await fetch("/environmentLayout.json", {
          cache: "no-cache",
        });
        if (!response.ok) return;
        const data = await response.json();
        setLayoutOverridesAll(data);
      } catch (error) {
        console.warn("Failed to load environment layout overrides", error);
      }
    };

    loadOverrides();
  }, []);

  // Select overrides for the current system id, if available.
  useEffect(() => {
    if (!layoutOverridesAll || !systemData?.id) {
      setLayoutOverrides(null);
      return;
    }

    const overridesForSystem = layoutOverridesAll[systemData.id];
    setLayoutOverrides(
      overridesForSystem && typeof overridesForSystem === "object"
        ? overridesForSystem
        : null
    );
  }, [layoutOverridesAll, systemData]);

  useEffect(() => {
    const handleMouseMove = (event) => {
      const unityContainer = document.querySelector(".content");
      if (!unityContainer) return;

      const rect = unityContainer.getBoundingClientRect();
      const headerHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--app-header-height"
        )
      );
      const resolvedHeaderHeight = Number.isFinite(headerHeight)
        ? headerHeight
        : 0;
      const edgeHoverWidth = 120;
      const edgeHoverHeight = 108;

      latestMousePositionRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      const x = latestMousePositionRef.current.x;
      const y = latestMousePositionRef.current.y;
      const contentHeight = Math.max(0, rect.height - resolvedHeaderHeight);
      const yInUnity = y - resolvedHeaderHeight;
      const withinUnity = yInUnity >= 0 && yInUnity <= contentHeight;

      const nextHover = {
        left: withinUnity && x <= edgeHoverWidth,
        right: withinUnity && x >= rect.width - edgeHoverWidth,
        top: withinUnity && yInUnity <= edgeHoverHeight,
        bottom: withinUnity && yInUnity >= contentHeight - edgeHoverHeight,
      };

      const prevHover = edgeHoverRef.current;
      if (
        prevHover.left !== nextHover.left ||
        prevHover.right !== nextHover.right ||
        prevHover.top !== nextHover.top ||
        prevHover.bottom !== nextHover.bottom
      ) {
        edgeHoverRef.current = nextHover;
        setEdgeHover(nextHover);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = () => {
      if (currentElement && currentElement.targetEnvironmentID) {
        setCursorStyle("zoom-in");
      } else if (currentElement) {
        setCursorStyle("pointer");
      } else {
        setCursorStyle("move");
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [currentElement]);

  const handleMouseDown = (event) => {
    dragStart.current = { x: event.clientX, y: event.clientY };
    setFocusedElement(null);
    setLabelPosition(null);
  };

  const handleMouseUp = (event) => {
    const threshold = 5;
    const dx = event.clientX - dragStart.current.x;
    const dy = event.clientY - dragStart.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= threshold) {
      if (currentElement) {
        setFocusedElement(currentElement);
      } else {
        setFocusedElement(null);
      }
      const { offsetX, offsetY } = event.nativeEvent;
      setLabelPosition({ x: offsetX + playerOffset, y: offsetY });
    }
  };

  useEffect(() => {
    if (!currentElement) {
      clearTimeout(focusTimeoutRef.current);
      setFocusedElement(null);
      setLabelPosition(null);
      return;
    }

    clearTimeout(focusTimeoutRef.current);

    focusTimeoutRef.current = setTimeout(() => {
      setFocusedElement(currentElement);
      const { x, y } = latestMousePositionRef.current;
      setLabelPosition({ x, y });
    }, 300);

    return () => {
      clearTimeout(focusTimeoutRef.current);
    };
  }, [currentElement]);

  const doneRecording = () => {
    sendMessageToUnity("EventDispatcher", "RecordingStopped");
  };

  const assessmentComplete = () => {
    sendMessageToUnity("EventDispatcher", "CompleteAssessment");
  };

  const changeEnvironment = (environmentId) => {
    sendMessageToUnity("EventDispatcher", "ChangeEnvironment", environmentId);
  };

  const startGuide = () => {
    sendMessageToUnity("EventDispatcher", "LoadGuide", experienceData.guideId);
  };

  const rotateCamera = (direction) => {
    sendMessageToUnity("EventDispatcher", "RotateCamera", direction);
  };

  const getDisplayName = (elementData) => {
    return !elementData.targetEnvironmentID
      ? `${elementData.labelData.displayName}`
      : `${elementData.labelData.displayName} 🔍`;
  };

  const getSubtitle = (elementData) => {
    return elementData.targetEnvironmentID && environmentsVisited < 6
      ? "Scroll or pinch to zoom in."
      : null;
  };

  const isMobile =
    typeof window !== "undefined" &&
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  const showEnvironmentMap = Boolean(
    systemData &&
      isCurrentGameState([
        GAME_STATES.ViewMode,
        GAME_STATES.ViewModeElementSelected,
        GAME_STATES.ZoomingIn,
        GAME_STATES.ZoomingOut,
      ])
  );
  const showElementLabelPanel = Boolean(
    focusedElement &&
      labelPosition &&
      isCurrentGameState([GAME_STATES.ViewModeElementSelected])
  );
  const isElementFocusedGameState = isCurrentGameState([
    GAME_STATES.ViewModeElementFocused,
  ]);
  const focusedPanelElementCandidate = focusedElement || currentElement;
  const showEdgeCameraControls = isCurrentGameState([
    GAME_STATES.ViewMode,
    GAME_STATES.ViewModeElementSelected,
  ]);
  const showFocusedElementPanel = Boolean(
    focusedPanelElementLocked && isElementFocusedGameState
  );

  useEffect(() => {
    if (!isElementFocusedGameState) {
      setFocusedPanelElementLocked(null);
      return;
    }

    if (!focusedPanelElementLocked && focusedPanelElementCandidate) {
      setFocusedPanelElementLocked(focusedPanelElementCandidate);
    }
  }, [
    focusedPanelElementLocked,
    focusedPanelElementCandidate,
    isElementFocusedGameState,
  ]);

  if (isMobile) {
    return <MobileFallback isPhone={isPhone} />;
  }

  return (
    <div
      className="content"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <StatusOverlays
        showEnvironmentLabel={showEnvironmentLabel}
        envLabelBottom={envLabelBottom}
        playerOffset={playerOffset}
        overlayPadding={overlayPadding}
        overlayWidth={overlayWidth}
        currentEnvironment={currentEnvironment}
        instructionsVisible={instructionsVisible}
        topMessageOffset={topMessageOffset}
        currentInsight={currentInsight}
        showControlsOverlay={showControlsOverlay}
        isPhone={isPhone}
        currentGameState={currentGameState}
      />

      <UnityCanvas
        unityProvider={unityProvider}
        cursorStyle={cursorStyle}
        playerOffset={playerOffset}
      />

      {showEdgeCameraControls && (
        <EdgeCameraControls
          edgeHover={edgeHover}
          edgeControlWidth={edgeControlWidth}
          edgeControlHeight={edgeControlHeight}
          rotateCamera={rotateCamera}
        />
      )}

      {!embed && (
        <ViewerHeader system={systemData} />
      )}

      <Box
        component="div"
        sx={{
          position: "absolute",
          transform: showElementLabelPanel ? "translateY(0px)" : "translateY(8px)",
          opacity: showElementLabelPanel ? 1 : 0,
          transition: "opacity 300ms ease, transform 300ms ease",
          pointerEvents: "none",
          zIndex: 10,
          visibility: showElementLabelPanel ? "visible" : "hidden",
        }}
      >
        {showElementLabelPanel && (
          <Label
            key={focusedElement.id}
            labelPosition={labelPosition}
            displayName={getDisplayName(focusedElement)}
            subtitle={getSubtitle(focusedElement)}
            alignment="auto"
            offsetLeft={0}
          />
        )}
      </Box>

      {showFocusedElementPanel && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: "var(--app-header-height, 0px)",
              right: 0,
              width: isPhone ? "100%" : "50%",
              height: "calc(100% - var(--app-header-height, 0px))",
              background: isPhone
                ? "linear-gradient(180deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.56) 68%, rgba(0, 0, 0, 0) 100%)"
                : "linear-gradient(270deg, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.62) 56%, rgba(0, 0, 0, 0) 100%)",
              pointerEvents: "none",
              zIndex: 15,
            }}
          />

          <Box
            sx={{
              position: "absolute",
              top: "var(--app-header-height, 0px)",
              right: 0,
              width: isPhone ? "100%" : "50%",
              height: "calc(100% - var(--app-header-height, 0px))",
              color: "#fff",
              pointerEvents: "none",
              zIndex: 16,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                px: isPhone ? 2.5 : 4,
                py: isPhone ? 2.5 : 4,
                width: isPhone ? "100%" : "60%",
                mx: "auto",
                textAlign: "left",
                transform: isPhone ? "translateY(-12%)" : "translateY(-22%)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ opacity: 0.78, letterSpacing: "0.08em" }}
              >
                ELEMENT
              </Typography>
              <Typography variant={isPhone ? "h5" : "h4"} sx={{ mt: 0.5 }}>
                {getDisplayName(focusedPanelElementLocked)}
              </Typography>
              {focusedPanelElementLocked?.labelData?.description && (
                <Typography variant="body1" sx={{ mt: 1.5, textAlign: "left" }}>
                  {focusedPanelElementLocked.labelData.description}
                </Typography>
              )}
            </Box>
          </Box>

          <Box
            component="div"
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: "calc(var(--app-header-height, 0px) + 14px)",
              right: "max(14px, env(safe-area-inset-right, 0px))",
              width: isPhone ? 44 : 54,
              height: isPhone ? 44 : 54,
              borderRadius: "999px",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              backgroundColor: "rgba(0, 0, 0, 0.42)",
              color: "#fff",
              fontSize: isPhone ? "1.9rem" : "2.2rem",
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 17,
              pointerEvents: "none",
              boxShadow: "0 8px 22px rgba(0, 0, 0, 0.35)",
              backdropFilter: "blur(4px)",
              WebkitBackdropFilter: "blur(4px)",
              transition:
                "background-color 160ms ease, border-color 160ms ease, transform 160ms ease",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.58)",
                borderColor: "rgba(255, 255, 255, 0.65)",
                transform: "scale(1.05)",
              },
            }}
          >
            ×
          </Box>
        </>
      )}

      {showEnvironmentMap && (
        <EnvironmentMap
          systemData={systemData}
          currentEnvironmentId={currentEnvironment?.id}
          changeEnvironment={changeEnvironment}
          layoutOverrides={layoutOverrides}
        />
      )}

      <VoiceRecorder
        doneRecording={doneRecording}
        assessmentComplete={assessmentComplete}
        ref={voiceRecorderRef}
      />

      {systemLoaded && <StartGuideDialog startGuide={startGuide} />}
      <MicAccessPopup />
    </div>
  );
};

const UnityWebViewer = (props) => {
  return (
    <GameStateProvider>
      <UnityWebViewerContent {...props} />
    </GameStateProvider>
  );
};

export default UnityWebViewer;
